import React, { useState, useEffect, useRef } from "react";
import { Button } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useDispatch, useSelector } from "react-redux";
import WarningIcon from "@mui/icons-material/Warning";
import { adcard_actions, cart_actions } from "../../../redux-store";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { get_promotion_prize } from "../../../utils";
import useDebounce from "../../../components/pages/Dashboard/components/AdWorkShop/hooks/useDebounce";
import { formatPrice } from "../../../utils/formatPrice";

const useStyles = makeStyles(() => ({
  outOfStockLabel: {
    padding: "10px",
    backgroundColor: "#898989",
    color: "white",
    textAlign: "center",
    fontWeight: "bold",
    borderRadius: "4px",
  },
  qtyBox: {
    marginBottom: "10px",
  },
  quantityExceedPrompt: {
    display: "flex",
    alignItems: "center",
    color: "#d32f2f",
    backgroundColor: "#fdecea",
    padding: "10px",
    borderRadius: "5px",
    marginTop: "10px",
    fontWeight: "bold",
    fontSize: "12px",
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    "& svg": {
      marginRight: "8px",
    },
  },
}));

const ProductInfoCard = ({ data }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state?.auth);
  const cart = useSelector((state) => state.cart.items);

  const [quantity, setQuantity] = useState(1);
  const [quantityExceed, setQuantityExceed] = useState(false);
  const [isInCart, setIsInCart] = useState(false);
  const debouncedQuantity = useDebounce(quantity, 500);
  const [quantityChanged, setQuantityChanged] = useState(false);
  const [isSharedLinkActive, setIsSharedLinkActive] = useState(
    data?.isSharedLinkActive || false
  );

  useEffect(() => {
    const cartItem = cart?.find((el) => el?.product?._id === data?._id);
    if (cartItem) {
      setQuantity(cartItem.quantity);
      setIsInCart(true);
    } else {
      setIsInCart(false);
    }
  }, [cart, data]);

  useEffect(() => {
    setIsSharedLinkActive(data?.isSharedLinkActive || false);
  }, [data]);

  useEffect(() => {
    if (quantityChanged) {
      updateCartHandler(debouncedQuantity);
      setQuantityChanged(false);
    }
  }, [debouncedQuantity]);

  const updateCartHandler = async (newQuantity) => {
    if (quantityExceed) {
      return;
    }
    const cartItem = cart?.find((el) => el?.product?._id === data?._id);
    if (cartItem) {
      try {
        await dispatch(
          cart_actions.update_cart_item(cartItem, { quantity: newQuantity })
        );
        setQuantity(newQuantity);
      } catch (error) {
        toast.error("Failed to update cart");
      }
    }
  };

  const handleQuantityChange = (newQuantity) => {
    if (newQuantity <= data.stock_quantity) {
      setQuantityExceed(false);
      setQuantity(newQuantity);
      setQuantityChanged(true);
    } else {
      setQuantityExceed(true);
    }
  };

  const addToCartHandler = () => {
    if (quantityExceed) {
      return;
    }

    // if (
    //   cart.length > 0 &&
    //   cart[0]?.product?.merchant_user !== data?.merchant_user?._id
    // ) {
    //   toast.error(
    //     "Please complete your purchase first before buying from a different Retailer"
    //   );
    //   return;
    // }
    dispatch(cart_actions.add_to_cart({ product: data, quantity }));
    setIsInCart(true);
  };

  const addDate = () => {
    const date = new Date();
    const answer = date.setDate(date.getDate() + 30);
    return new Date(answer)
      .toISOString()
      .replace(/T.*/, "")
      .split("-")
      .join("-");
  };

  const initAdcard = (i) => {
    dispatch(
      adcard_actions.create_adcard({
        product: i._id,
        expiry_date: addDate(),
      })
    ).then(() => {
      setIsSharedLinkActive(true);
    });
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="row">
          <div className="col-12 col-sm-5 mb-3 mb-sm-0 m-sm-0 p-sm-0">
            <div style={{ height: 305 }}>
              {data?.product_images?.[0]?.image ? (
                <img
                  src={data?.product_images?.[0]?.image}
                  className="w-100 h-100"
                  style={{ objectFit: "cover" }}
                  alt={data?.product_name}
                />
              ) : (
                <div className="d-flex align-items-center justify-content-center w-100 h-100 bg-light">
                  <i className="fa fa-image display-4" aria-hidden="true"></i>
                </div>
              )}
            </div>
          </div>
          <div className="col-12 col-sm-7">
            <div className="d-flex flex-column justify-content-between h-100">
              <Link to={`/product/${data?._id}`}>
                <h4 className="mb-2">
                  {data?.product_name?.length > 40
                    ? `${data?.product_name.substring(0, 40)}...`
                    : data?.product_name}
                </h4>
              </Link>
              <div className="d-flex flex-column mb-2">
                <strong>Description</strong>
                <span>
                  {data?.product_description?.length > 50
                    ? `${data?.product_description?.substring(0, 50)}...`
                    : data?.product_description}
                </span>
              </div>
              <div className="d-flex flex-column flex-wrap mb-2">
                <span>
                  <strong>Price:</strong> {formatPrice(data?.product_price)}
                </span>
                {user?.user?.role === "member" && (
                  <>
                    <span>
                      <strong>VAC Price:</strong>{" "}
                      {formatPrice(data?.winner_price) || "N/A"}
                    </span>
                    <span>
                      <strong>VAC Contest:</strong>
                      {data?.competition_month
                        ? new Date(data?.competition_month).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </>
                )}
              </div>
              {user?.user?.role === "member" ? (
                <small className="text-muted">
                  Promotion Prize &mdash;{" "}
                  {formatPrice(get_promotion_prize(data))} / Item
                </small>
              ) : (
                <span className="mb-2">
                  <strong>Stock Quantity:</strong> {data.stock_quantity}
                </span>
              )}
              <hr />
              {user?.user?.role === "member" ? (
                <Button
                  className="btn btn-solid"
                  onClick={() => {
                    initAdcard(data);
                  }}
                  disabled={isSharedLinkActive}
                >
                  {isSharedLinkActive ? "Uploaded" : "Upload EAC"}
                </Button>
              ) : data.stock_quantity > 0 ? (
                <>
                  <div className={`qty-box ${classes.qtyBox}`}>
                    <div className="input-group">
                      <span className="input-group-prepend">
                        <button
                          type="button"
                          className="btn quantity-left-minus"
                          onClick={() => handleQuantityChange(quantity - 1)}
                          disabled={quantity <= 1}
                        >
                          <i className="fa fa-angle-left" />
                        </button>
                      </span>
                      <input
                        type="text"
                        name="quantity"
                        value={quantity}
                        readOnly
                        className="form-control input-number"
                        disabled={quantityExceed}
                      />
                      <span className="input-group-prepend">
                        <button
                          className="btn quantity-right-plus"
                          onClick={() => handleQuantityChange(quantity + 1)}
                          disabled={quantityExceed}
                        >
                          <i className="fa fa-angle-right" />
                        </button>
                      </span>
                    </div>
                    {quantityExceed && (
                      <div className={classes.quantityExceedPrompt}>
                        <WarningIcon />
                        Current quantity available is {data.stock_quantity}. You
                        can't order more than that.
                      </div>
                    )}
                  </div>
                  <Button
                    className="btn btn-solid"
                    onClick={addToCartHandler}
                    disabled={isInCart || quantityExceed}
                  >
                    {isInCart ? "Added to Cart" : "Add to Cart"}
                  </Button>
                </>
              ) : (
                <div className={classes.outOfStockLabel}>Out of Stock</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductInfoCard;
